import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useClassName } from 'common/hooks';
import {
  ContentfulRichText,
  optimizeContentfulImageUrl,
} from 'common/components';
import { track } from 'utils/analytics';

import './price-options-selector.less';

const PriceOptionsSelector = ({
  options,
  banner,
  bannerIcon,
  onClick,
  layout,
}) => {
  const classNames = useClassName('PriceOptionsSelector');
  const primaryIndex = options.findIndex((option) => option.type === 'primary');
  // When no option is flagged "primary" (e.g. equal-price options with no
  // "best value" badge), default to the last option rather than the first.
  const defaultIndex = primaryIndex >= 0 ? primaryIndex : options.length - 1;
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  if (!options?.length) return null;

  const selected = options[selectedIndex];
  const isList = layout === 'list';

  const handleGetPasses = () => {
    track('Price Option Get Passes', {
      name: selected.name,
      link: selected.link,
    });
    if (onClick) onClick(selected.link, selected.name);
  };

  return (
    <div className={classNames(['container', isList && 'container--list'])}>
      {banner && (
        <div className={classNames('banner')}>
          {bannerIcon?.fields?.file && (
            <img
              src={optimizeContentfulImageUrl(
                bannerIcon.fields.file.url,
                bannerIcon.fields.file.contentType
              )}
              alt=""
              aria-hidden="true"
              className={classNames('banner-icon')}
            />
          )}
          {banner}
        </div>
      )}
      <div className={classNames('cards')}>
        {options.map((option, index) => {
          const isSelected = index === selectedIndex;
          return (
            <div
              key={option.name}
              role="radio"
              tabIndex={0}
              aria-checked={isSelected}
              onClick={() => setSelectedIndex(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setSelectedIndex(index);
                }
              }}
              className={classNames(['card', isSelected && 'card--selected'])}>
              {option.type === 'primary' && (
                <span className={classNames('badge')}>Best Value</span>
              )}
              <span className={classNames('radio')} />
              {isList ? (
                <>
                  <div className={classNames('row-info')}>
                    <span className={classNames('name')}>{option.name}</span>
                    {typeof option.description === 'string' && (
                      <span className={classNames('description')}>
                        {option.description}
                      </span>
                    )}
                  </div>
                  <div className={classNames('row-price')}>
                    {option.price && (
                      <strong className={classNames('price')}>
                        {option.price}
                      </strong>
                    )}
                    {option.oldPrice && (
                      <s className={classNames('old-price')}>
                        {option.oldPrice}
                      </s>
                    )}
                    {option.priceLabel && (
                      <span className={classNames('price-label')}>
                        {option.priceLabel}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <span className={classNames('name')}>{option.name}</span>
                  {option.price && (
                    <div className={classNames('price-row')}>
                      <strong className={classNames('price')}>
                        {option.price}
                      </strong>
                      {option.oldPrice && (
                        <s className={classNames('old-price')}>
                          {option.oldPrice}
                        </s>
                      )}
                    </div>
                  )}
                  {option.priceLabel && (
                    <span className={classNames('price-label')}>
                      {option.priceLabel}
                    </span>
                  )}
                  {option.priceLabel && option.description && (
                    <span className={classNames('divider')} />
                  )}
                  {option.description &&
                    (typeof option.description === 'string' ? (
                      <div className={classNames('description')}>
                        {option.description}
                      </div>
                    ) : (
                      <div className={classNames('description')}>
                        <ContentfulRichText field={option.description} />
                      </div>
                    ))}
                </>
              )}
            </div>
          );
        })}
      </div>
      <a
        href={selected.link}
        onClick={handleGetPasses}
        className={classNames('cta')}>
        Get Passes
      </a>
    </div>
  );
};

PriceOptionsSelector.propTypes = {
  options: PropTypes.array.isRequired,
  banner: PropTypes.string,
  bannerIcon: PropTypes.object,
  onClick: PropTypes.func,
  // "cards" (side-by-side cards) or "list" (compact rows, mobile sheet).
  layout: PropTypes.oneOf(['cards', 'list']),
};

PriceOptionsSelector.defaultProps = {
  banner: null,
  bannerIcon: null,
  onClick: null,
  layout: 'cards',
};

export { PriceOptionsSelector };
export default PriceOptionsSelector;
